import React, { memo, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Timeline, TimelineEffect } from '@xzdarcy/react-timeline-editor';
import { CustomRender0 } from './timelineRenderer';
import { hrsToMnsToSecToMlSec } from 'utils';
import useVideo from 'hooks/video';

// background: `url(${background})`
export const mockEffect: Record<string, TimelineEffect> = {
  effect0: {
    id: 'effect0',
    name: '效果0'
  },
  effect1: {
    id: 'effect1',
    name: '效果1'
  }
};

const TimelineWrap = styled('div')<{
  background?: string;
  scaleWidth?: number;
  selected?: boolean;
}>(({ theme, background, scaleWidth }) => ({
  overflow: 'scroll',
  width: '100%',
  '& .timeline-editor-edit-row:has(>.timeline-editor-action)': {
    background: `url('${background}') !important`,
    backgroundSize: `${scaleWidth}px 70% !important`,
    backgroundRepeat: 'no-repeat !important',
    backgroundPosition: '20px center !important',
    '& .timeline-editor-action': {
      background: 'rgb(255 0 0 / 20%) !important',
      boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)'
    }
  }
}));

interface TimelineWrapperProps {
  setCropDisable: (disable: boolean) => void;
  videoRef: React.RefObject<HTMLVideoElement>;
  setIsPlaying: (playing: boolean) => void;
  setCropState: (state: any) => void;
  handleUpdateClip: (data: any) => void;
  timelineRef: React.RefObject<any>;
  duration: number;
  setActivityName: React.Dispatch<React.SetStateAction<any>>;
  scale: number;
  zoomCount: number;
  timelineWrapperRef: React.RefObject<any>;
  scaleWidth: number;
  getActionRender: any;
  timelineBackground: string;
  activityData: any[];
  activityDataPointer: number;
  domRef: React.RefObject<any>;
  timelineState: React.RefObject<any>;
  setActivityData: (data: any) => void;
  setActivityDataPointer: (pointer: number) => void;
}

function TimelineWrapper({
  setCropDisable,
  videoRef,
  setIsPlaying,
  setCropState,
  handleUpdateClip,
  setActivityName,
  getActionRender,
  timelineRef,
  duration,
  scale,
  zoomCount,
  timelineWrapperRef,
  scaleWidth,
  timelineBackground,
  activityData,
  activityDataPointer,
  domRef,
  timelineState,
  setActivityData,
  setActivityDataPointer
}: TimelineWrapperProps) {
  const { seek } = useVideo();
  const lastKnownTimeRef = useRef<number | null>(null);
  const lastActivityPointerRef = useRef<number>(activityDataPointer);
  const animationFrameRef = useRef<number | null>(null);
  const isPlayingRef = useRef<boolean>(false);
  const isBufferingRef = useRef<boolean>(false);

  // Add effect to sync video and timeline when activityDataPointer changes
  useEffect(() => {
    // If activityDataPointer changed, it's likely due to forward/backward navigation
    if (lastActivityPointerRef.current !== activityDataPointer) {
      lastActivityPointerRef.current = activityDataPointer;

      // Wait a moment for the timeline to update
      setTimeout(() => {
        if (videoRef.current && timelineState.current) {
          const currentTimelineTime = timelineState.current.getTime();
          videoRef.current.currentTime = currentTimelineTime;
          lastKnownTimeRef.current = currentTimelineTime;
        }
      }, 50);
    }
  }, [activityDataPointer, videoRef, timelineState]);

  // Add effect for smooth cursor movement using requestAnimationFrame
  useEffect(() => {
    const updateCursor = () => {
      if (
        videoRef.current &&
        timelineState.current &&
        isPlayingRef.current &&
        !isBufferingRef.current
      ) {
        timelineState.current.setTime(videoRef.current.currentTime);
        lastKnownTimeRef.current = videoRef.current.currentTime;
      }

      // Continue animation only if playing and not buffering
      if (isPlayingRef.current) {
        animationFrameRef.current = requestAnimationFrame(updateCursor);
      }
    };

    const handlePlay = () => {
      isPlayingRef.current = true;
      if (animationFrameRef.current === null) {
        animationFrameRef.current = requestAnimationFrame(updateCursor);
      }
    };

    const handlePause = () => {
      isPlayingRef.current = false;
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };

    const handleWaiting = () => {
      isBufferingRef.current = true;
      // When buffering starts, we temporarily pause cursor movement
    };

    const handlePlaying = () => {
      isBufferingRef.current = false;
      // When buffering ends, we resume cursor movement
      if (isPlayingRef.current && animationFrameRef.current === null) {
        animationFrameRef.current = requestAnimationFrame(updateCursor);
      }
    };

    const handleSeeking = () => {
      if (videoRef.current && timelineState.current) {
        // For seeking, update immediately without animation
        timelineState.current.setTime(videoRef.current.currentTime);
        lastKnownTimeRef.current = videoRef.current.currentTime;
      }
    };

    const videoElement = videoRef.current;
    if (videoElement) {
      // Set initial state
      isPlayingRef.current = !videoElement.paused;
      isBufferingRef.current = videoElement.readyState < 3; // HAVE_FUTURE_DATA

      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);
      videoElement.addEventListener('seeking', handleSeeking);
      videoElement.addEventListener('seeked', handleSeeking);
      videoElement.addEventListener('waiting', handleWaiting);
      videoElement.addEventListener('playing', handlePlaying);

      // If already playing and not buffering, start animation
      if (isPlayingRef.current && !isBufferingRef.current) {
        animationFrameRef.current = requestAnimationFrame(updateCursor);
      }
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('seeking', handleSeeking);
        videoElement.removeEventListener('seeked', handleSeeking);
        videoElement.removeEventListener('waiting', handleWaiting);
        videoElement.removeEventListener('playing', handlePlaying);
      }

      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [videoRef, timelineState]);

  // Memoize scroll handler
  const handleScroll = useCallback(
    (e: { scrollTop: number }) => {
      if (domRef.current) {
        domRef.current.scrollTop = e.scrollTop;
      }
    },
    [domRef]
  );

  // Validate and bound action times within the duration
  const validateAndBoundActionTimes = useCallback(
    (start: number, end: number) => {
      // Ensure start is not negative
      const validStart = Math.max(0, start);
      // Ensure end doesn't exceed duration
      const validEnd = Math.min(duration, end);
      // Ensure minimum clip length (e.g., 0.1 seconds)
      const minClipLength = 0.1;

      // If the clip would be too short, adjust end time
      if (validEnd - validStart < minClipLength) {
        // If we're close to the end of the video, adjust start instead
        if (validEnd >= duration - 0.1) {
          return [Math.max(0, duration - minClipLength), duration];
        }
        // Otherwise, extend the end, but don't exceed duration
        return [validStart, Math.min(validStart + minClipLength, duration)];
      }

      return [validStart, validEnd];
    },
    [duration]
  );

  // Memoize move end handler with boundary checks
  const handleActionMoveEnd = useCallback(
    ({ action, row, start, end }: any) => {
      // Apply boundary validation
      const [validStart, validEnd] = validateAndBoundActionTimes(start, end);

      // Check if we're trying to move beyond boundaries
      const isBeyondBoundaries = start < 0 || end > duration;

      // Update clip with valid times
      handleUpdateClip({
        activityId: row.id,
        clipId: action.id,
        start: validStart,
        end: validEnd
      });

      setCropState({
        action_id: action?.id,
        row_id: row?.id,
        start: validStart
      });

      timelineState.current.setTime(validStart);

      // If trying to go beyond boundaries, ensure timeline cursor stays in view
      if (isBeyondBoundaries && timelineState.current) {
        // Set timeline to valid start time
        timelineState.current.setTime(validStart);
        if (videoRef.current) {
          videoRef.current.currentTime = validStart;
          lastKnownTimeRef.current = validStart;
        }
      }
    },
    [
      handleUpdateClip,
      setCropState,
      timelineState,
      videoRef,
      duration,
      validateAndBoundActionTimes
    ]
  );

  // Memoize double click handler
  const handleDoubleClickAction = useCallback(
    (_e: any, { action, row }: any) => {
      const [validStart, validEnd] = validateAndBoundActionTimes(
        action?.start,
        action?.end
      );

      handleUpdateClip({
        activityId: row?.id,
        clipId: action?.id,
        start: validStart,
        end: validEnd
      });

      setCropState({
        action_id: action?.id,
        row_id: row?.id,
        start: validStart
      });

      timelineState.current.setTime(validStart);
    },
    [handleUpdateClip, setCropState, validateAndBoundActionTimes]
  );

  // Memoize action resize end handler with boundary checks
  const handleActionResizeEnd = useCallback(
    ({ action, row, start, end }: any) => {
      // Apply boundary validation
      const [validStart, validEnd] = validateAndBoundActionTimes(start, end);

      // Check if we're trying to resize beyond boundaries
      const isBeyondBoundaries = end > duration || start < 0;

      // Update clip with valid times
      handleUpdateClip({
        activityId: row?.id,
        clipId: action?.id,
        start: validStart,
        end: validEnd
      });

      setCropState({
        action_id: action?.id,
        row_id: row?.id,
        start: validStart
      });

      timelineState.current.setTime(validStart);

      // If trying to resize beyond boundaries, ensure timeline cursor stays in view
      if (isBeyondBoundaries && timelineState.current) {
        // If end time was adjusted (trying to go past duration),
        // set timeline to a position that keeps the clip in view
        const positionToUse =
          end > duration ? Math.max(0, validEnd - 1) : validStart;

        timelineState.current.setTime(positionToUse);
        if (videoRef.current) {
          videoRef.current.currentTime = positionToUse;
          lastKnownTimeRef.current = positionToUse;
        }
      }
    },
    [
      handleUpdateClip,
      setCropState,
      timelineState,
      videoRef,
      duration,
      validateAndBoundActionTimes
    ]
  );

  // Memoize click action handler
  const handleClickAction = useCallback(
    (_e: any, { action, row }: any) => {
      setActivityName(action);
      // Pause the video
      setIsPlaying(false);
      isPlayingRef.current = false;
      videoRef.current?.pause();

      // Validate action times
      const [validStart, _] = validateAndBoundActionTimes(
        action?.start,
        action?.end
      );

      // Set the crop state
      setCropState({
        action_id: action?.id,
        row_id: row?.id,
        start: validStart
      });

      timelineState.current.setTime(validStart);

      // Disable crop
      setCropDisable(false);

      // Synchronize timeline and video to the clip's start time
      if (timelineState.current) {
        timelineState.current.setTime(validStart);
      }

      // Seek video to the clip's start time
      if (videoRef.current) {
        videoRef.current.currentTime = validStart;
        lastKnownTimeRef.current = validStart;
      }
    },
    [
      setIsPlaying,
      videoRef,
      setCropState,
      setCropDisable,
      timelineState,
      validateAndBoundActionTimes
    ]
  );

  // We'll use the existing handlers instead of custom ones
  // The validateAndBoundActionTimes function will handle the boundary checks

  useEffect(() => {
    if (activityDataPointer === 0) return;

    const currentActivities = activityData[activityDataPointer] || [];
    const previousActivities = activityData[activityDataPointer - 1] || [];
    if (activityData.length === 1) {
      setActivityName(() => {
        return {
          data: { name: currentActivities.activity }
        };
      });
    } else if (currentActivities.length > previousActivities.length) {
      const newActivities = currentActivities.filter(
        (activity: any) =>
          !previousActivities.some((prev: any) => prev.id === activity.id)
      );

      if (newActivities.length > 0) {
        setActivityName(() => {
          return {
            data: { name: newActivities[newActivities.length - 1].activity }
          };
        });
      }
    }
  }, [activityData, activityDataPointer]);

  return (
    <TimelineWrap
      ref={timelineWrapperRef}
      scaleWidth={scaleWidth}
      background={timelineBackground}
      style={{
        padding: 0,
        overflowY: 'scroll',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}
    >
      {activityData[activityDataPointer] && (
        <Timeline
          onScroll={handleScroll}
          ref={timelineState}
          autoScroll={true}
          editorData={[
            ...(activityData[activityDataPointer].map((activity: any) => ({
              id: activity.id,
              actions: activity.clips.map((clip: any) => ({
                id: clip.id,
                start: Number(clip.start_time),
                end: Math.min(Number(clip.end_time), duration), // Ensure end time doesn't exceed duration
                effectId: 'effect1',
                data: {
                  src: clip.thumb_url,
                  name: activity.activity
                }
              }))
            })) || [])
          ]}
          effects={mockEffect}
          style={{
            width: '100%',
            height: '100%',
            scrollbarColor: 'transparent'
          }}
          onChange={() => {}}
          getActionRender={getActionRender}
          scaleSplitCount={zoomCount === 0 ? 8 : zoomCount === 1 ? 4 : 2}
          getScaleRender={scale => hrsToMnsToSecToMlSec(scale)}
          scale={scale}
          maxScaleCount={duration / scale || 1}
          minScaleCount={duration / scale || 1}
          scaleWidth={
            timelineRef.current?.clientWidth
              ? timelineRef.current?.clientWidth / 10 - 25
              : 1
          }
          dragLine={true}
          hideCursor={false}
          rowHeight={40}
          onActionMoveEnd={handleActionMoveEnd}
          onDoubleClickAction={handleDoubleClickAction}
          onActionResizeEnd={handleActionResizeEnd}
          onActionResizing={data => data.end - data.start > 0.1}
          startLeft={20}
          onClickAction={handleClickAction}
        />
      )}
    </TimelineWrap>
  );
}

export default memo(TimelineWrapper);
